import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { User, Settings } from "lucide-react";
import AccountSettings from "@/pages/AccountSettings";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { fmtShortDate } from "@/lib/format";
import { Field } from "@/components/AuthShell";
import AvatarUpload from "@/components/AvatarUpload";
import { InstallCard } from "@/components/InstallGuide";
import PushToggle from "@/components/PushToggle";
import { ModeToggle } from "@/lib/mode";
import { Avatar, AVATAR_ICONS, ErrorBox, PageHeader, Spinner, Stat } from "@/components/Bits";

export default function Profile() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("onglet") === "parametres" ? "settings" : "profile";
  const { user } = useAuth();
  return (
    <div className="space-y-6">
      <PageHeader eyebrow="Mon compte" title={user.display_name} />
      <div className="flex gap-2 rounded-2xl bg-white/[0.04] p-1" role="tablist" data-testid="profile-tabs">
        {[["profile", "Mon profil", User], ["settings", "Paramètres", Settings]].map(([k, l, I]) => (
          <button key={k} role="tab" aria-selected={tab === k} onClick={() => setParams(k === "settings" ? { onglet: "parametres" } : {}, { replace: true })}
            className={`flex flex-1 items-center justify-center gap-2 rounded-xl py-2.5 text-sm font-bold transition-colors ${tab === k ? "bg-[#B7F52A] text-[#101310]" : "text-slate-300 hover:text-white"}`}
            data-testid={`profile-tab-${k}`}><I size={15} /> {l}</button>))}
      </div>
      {tab === "settings" ? <AccountSettings embedded /> : <ProfileMain />}
    </div>
  );
}

function ProfileMain() {
  const { user, setUser, tz } = useAuth();
  const [stats, setStats] = useState(null);
  const [name, setName] = useState(user.display_name);
  const [handle, setHandle] = useState(user.username || "");
  const [avatar, setAvatar] = useState(user.avatar?.startsWith("img:") ? undefined : user.avatar);
  const [error, setError] = useState("");
  useEffect(() => { api.get("/me/stats").then(({ data }) => setStats(data)).catch((e) => setError(errMsg(e))); }, []);

  const save = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const base = { display_name: name, ...(handle && handle !== user.username ? { username: handle } : {}) };
      const { data } = await api.patch("/me", avatar === undefined ? base : { ...base, avatar });
      setUser(data);
      toast.success("Profil mis à jour");
    } catch (err) { setError(errMsg(err)); }
  };

  return (
    <div className="space-y-8">
      <p className="text-xs text-slate-400">Membre depuis le {fmtShortDate(user.created_at, tz)}</p>
      <InstallCard />
      <section className="mpl-card space-y-2 p-4" data-testid="profile-mode">
        <p className="text-sm font-bold">Affichage</p>
        <p className="text-xs text-slate-400">Auto : Jour quand il fait jour là où tu es, Nuit après le coucher du soleil. Tu peux aussi forcer Jour ou Nuit, gardé sur cet appareil.</p>
        <ModeToggle testId="profile-mode-toggle" />
      </section>
      <section className="mpl-card space-y-2 p-4" data-testid="profile-push">
        <p className="text-sm font-bold">Notifications sur ce téléphone</p>
        <p className="text-xs text-slate-400">Rappel des matchs à midi, résultats, classement, défis et mentions. Sur iPhone, active-les depuis l'icône installée.</p>
        <PushToggle />
      </section>
      {!stats ? <Spinner /> : (
        <section className="grid grid-cols-2 gap-3 sm:grid-cols-3" data-testid="profile-stats">
          <Stat label="Points (toutes prédictions)" value={stats.points} accent testId="profile-points" />
          <Stat label="Correctes" value={stats.correct} testId="profile-correct" />
          <Stat label="Taux de réussite" value={stats.accuracy != null ? `${Math.round(stats.accuracy)} %` : "—"} testId="profile-accuracy" />
          <Stat label="Meilleure série" value={stats.best_streak} testId="profile-best-streak" />
          <Stat label="Série actuelle" value={stats.current_streak} testId="profile-current-streak" />
          <Stat label="Ligues" value={stats.leagues} testId="profile-leagues" />
        </section>
      )}
      <p className="text-xs text-slate-500">Le taux de réussite est calculé sur les prédictions résolues ; les matchs annulés ou en attente ne comptent pas comme des erreurs. Les points par ligue figurent dans chaque classement.</p>
      <form onSubmit={save} className="mpl-card space-y-5 p-5" data-testid="profile-form">
        <Field label="Nom d'affichage">
          <input className="mpl-input" minLength={2} maxLength={30} value={name} onChange={(e) => setName(e.target.value)} data-testid="profile-name-input" />
        </Field>
        <Field label="Pseudo (unique, pour te retrouver dans les défis et les mentions)">
          <div className="flex items-center gap-1"><span className="font-bold text-[#B7F52A]">@</span>
            <input className="mpl-input" minLength={2} maxLength={20} pattern="[A-Za-z0-9_]{2,20}" title="2 à 20 caractères : lettres sans accent, chiffres ou _" value={handle}
              onChange={(e) => setHandle(e.target.value.replace(/[^A-Za-z0-9_]/g, ""))} data-testid="profile-username-input" /></div>
          <p className="mt-1 text-[11px] text-slate-500">2 à 20 caractères : lettres sans accent, chiffres ou _.</p>
        </Field>
        <div>
          <p className="mb-2 text-xs font-semibold text-slate-300">Photo de profil</p>
          <AvatarUpload />
        </div>
        <div>
          <p className="mb-2 text-xs font-semibold text-slate-300">Ou un avatar par défaut</p>
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={() => setAvatar(null)} data-testid="avatar-option-none" className={`rounded-full p-0.5 ${avatar === null ? "ring-2 ring-[#B7F52A]" : ""}`}>
              <Avatar user={{ display_name: name }} size={44} />
            </button>
            {Object.keys(AVATAR_ICONS).map((k) => (
              <button type="button" key={k} onClick={() => setAvatar(k)} data-testid={`avatar-option-${k}`} className={`rounded-full p-0.5 ${avatar === k ? "ring-2 ring-[#B7F52A]" : ""}`}>
                <Avatar user={{ avatar: k }} size={44} />
              </button>
            ))}
          </div>
        </div>
        {error && <ErrorBox message={error} />}
        <button className="mpl-btn-primary" data-testid="profile-save-button">Enregistrer</button>
      </form>
    </div>
  );
}
